import { useEffect, useRef, useState } from "react";
import { Camera, ImageUp, Sparkles, X, Plus, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg, resizeImage } from "@/lib/api";
import DietFilters from "@/components/app/DietFilters";
import RecipeCard from "@/components/app/RecipeCard";
import ScanImage from "@/components/app/ScanImage";

export default function ScanTab({ initialScan, onOpen, isFav, toggleFav }) {
  const api = useApi();
  const camRef = useRef(null);
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [diets, setDiets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scan, setScan] = useState(initialScan);
  const [ingredients, setIngredients] = useState(initialScan?.ingredients?.map((i) => i.name) || []);
  const [recipes, setRecipes] = useState(initialScan?.recipes || []);
  const [newIng, setNewIng] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const pick = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setScan(null); setRecipes([]); setIngredients([]);
  };

  const analyze = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const blob = await resizeImage(file);
      const fd = new FormData();
      fd.append("file", blob, "frigo.jpg");
      fd.append("diets", diets.join(","));
      const res = await api("post", "/scan", fd);
      setScan(res);
      setIngredients(res.ingredients.map((i) => i.name));
      setRecipes(res.recipes);
      setDirty(false);
      if (!res.ingredients.length) toast("Non ho riconosciuto alimenti: prova una foto più nitida");
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };

  const regenerate = async () => {
    setLoading(true);
    try {
      const res = await api("post", "/recipes/regenerate", { ingredients, diets, scan_id: scan?.id });
      setRecipes(res.recipes);
      setDirty(false);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };

  const addIng = (e) => {
    e.preventDefault();
    const v = newIng.trim();
    if (v && !ingredients.some((i) => i.toLowerCase() === v.toLowerCase())) { setIngredients([...ingredients, v]); setDirty(true); }
    setNewIng("");
  };
  const removeIng = (n) => { setIngredients(ingredients.filter((i) => i !== n)); setDirty(true); };
  const changeDiets = (d) => { setDiets(d); if (scan || ingredients.length) setDirty(true); };

  const shown = diets.includes("veloce") ? recipes.filter((r) => r.time_minutes <= 20) : recipes;
  const hasResult = scan || recipes.length > 0;

  return (
    <div data-testid="scan-tab">
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">Cosa c'è nel frigo?</h1>
      <p className="mt-2 text-[#4F5D52]">Scatta o carica una foto, l'AI trova gli ingredienti e ti propone le ricette.</p>

      <div className="mt-8 grid lg:grid-cols-[1fr_1.1fr] gap-6 lg:gap-10 items-start">
        <div className="relative aspect-[4/3] overflow-hidden rounded-3xl border-2 border-dashed border-[#2E6F40]/25 bg-[#E8F3EA]/50" data-testid="photo-dropzone">
          {preview ? <img src={preview} alt="Foto frigo" className="h-full w-full object-cover" />
            : scan ? <ScanImage scanId={scan.id} className="h-full w-full object-cover" />
            : (
              <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center text-[#2E6F40]">
                <Camera className="h-12 w-12" strokeWidth={1.5} />
                <p className="font-semibold">Nessuna foto ancora</p>
                <p className="text-sm text-[#4F5D52]">Inquadra bene i ripiani per risultati migliori</p>
              </div>
            )}
          {loading && file && !scan && (
            <div className="absolute inset-0 bg-[#1C241E]/40 flex items-end" data-testid="scan-loading">
              <div className="scan-line absolute inset-x-0 top-0 h-[10%] bg-gradient-to-b from-transparent via-[#E8F3EA]/70 to-transparent" />
              <p className="m-4 rounded-full bg-white/90 px-4 py-2 text-sm font-semibold flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />Analisi degli ingredienti in corso...
              </p>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <input ref={camRef} type="file" accept="image/*" capture="environment" hidden data-testid="camera-input" onChange={(e) => pick(e.target.files[0])} />
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden data-testid="file-input" onChange={(e) => pick(e.target.files[0])} />
          <div className="flex flex-wrap gap-3">
            <button data-testid="camera-take-photo-button" onClick={() => camRef.current.click()} disabled={loading}
              className="inline-flex items-center gap-2 rounded-full bg-[#1C241E] px-5 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-50">
              <Camera className="h-4 w-4" />Scatta foto
            </button>
            <button data-testid="camera-upload-button" onClick={() => fileRef.current.click()} disabled={loading}
              className="inline-flex items-center gap-2 rounded-full border border-[#1C241E]/15 bg-white px-5 py-3 text-sm font-semibold transition-colors hover:bg-[#E8F3EA] disabled:opacity-50">
              <ImageUp className="h-4 w-4" />Carica dalla galleria
            </button>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[#4F5D52] mb-3">Preferenze alimentari</p>
            <DietFilters value={diets} onChange={changeDiets} />
          </div>

          {file && !scan && (
            <button data-testid="ai-scan-trigger-button" onClick={analyze} disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2E6F40] px-6 py-4 font-semibold text-white shadow-lg shadow-[#2E6F40]/25 transition-colors hover:bg-[#235732] disabled:opacity-60">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
              {loading ? "Sto guardando nel frigo..." : "Trova le ricette"}
            </button>
          )}

          {hasResult && (
            <div className="rounded-3xl border border-[#1C241E]/10 bg-white p-5" data-testid="ingredients-panel">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#2E6F40] mb-3">Ingredienti trovati ({ingredients.length})</p>
              <div className="flex flex-wrap gap-2">
                {ingredients.map((n) => (
                  <span key={n} data-testid="ingredient-chip-item" className="inline-flex items-center gap-1 rounded-full bg-[#E8F3EA] pl-3 pr-1.5 py-1 text-sm text-[#1A4226]">
                    {n}
                    <button data-testid={`remove-ingredient-${n}`} onClick={() => removeIng(n)} aria-label={`Rimuovi ${n}`} className="rounded-full p-0.5 hover:bg-white"><X className="h-3.5 w-3.5" /></button>
                  </span>
                ))}
              </div>
              <form onSubmit={addIng} className="mt-4 flex gap-2">
                <input data-testid="add-ingredient-input" value={newIng} onChange={(e) => setNewIng(e.target.value)} placeholder="Aggiungi un ingrediente..."
                  className="flex-1 rounded-full border border-[#1C241E]/15 bg-[#FDFBF7] px-4 py-2 text-sm outline-none focus:border-[#2E6F40]" />
                <button data-testid="add-ingredient-button" type="submit" className="rounded-full bg-[#E8F3EA] p-2.5 text-[#2E6F40] hover:bg-[#2E6F40] hover:text-white transition-colors"><Plus className="h-4 w-4" /></button>
              </form>
              {dirty && (
                <button data-testid="regenerate-recipes-button" onClick={regenerate} disabled={loading}
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#D95338] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#B83E25] disabled:opacity-60">
                  <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />Aggiorna ricette
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {hasResult && (
        <section className="mt-12">
          <h2 className="font-display text-2xl sm:text-3xl font-bold">Ricette consigliate</h2>
          {shown.length === 0 ? (
            <p className="mt-4 text-[#4F5D52]" data-testid="no-recipes">Nessuna ricetta con questi filtri. Prova ad aggiornare le ricette.</p>
          ) : (
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="recipes-grid">
              {shown.map((r, i) => <RecipeCard key={r.id} recipe={r} index={i} onOpen={onOpen} fav={isFav(r)} onFav={toggleFav} />)}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
